"use client";

import { useTranslations } from "next-intl";

interface VerifiedIconProps {
  size?: number;
  /** Vertical nudge to optically align with surrounding text baseline */
  nudge?: number;
}

/**
 * Easytiger custom verified badge with "Verified Professional" tooltip.
 * Used next to professional names across the directory. Client component
 * (it renders inside both server and client trees) so it can localize its
 * own aria/tooltip/sr-only text via next-intl.
 */
export default function VerifiedIcon({ size = 16, nudge = 1 }: VerifiedIconProps) {
  const t = useTranslations("a11y");
  return (
    <span
      tabIndex={0}
      className="group/verified relative inline-flex shrink-0"
      style={{ translate: `0 ${nudge}px`, paddingTop: 2 }}
      aria-label={t("verifiedProfessional")}
    >
      {/* Solid shield + checkmark, inlined as a single filled path (shield
          outline carved with the check) so it inherits text colour via
          `fill="currentColor"` and ships zero extra HTTP requests for a
          glyph that renders next to every pro name. */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 877 877"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="text-green"
        aria-hidden="true"
      >
        <path
          d="M426.45 38.5879C434.514 35.7704 443.32 35.8648 451.334 38.8701L743.667 148.495C757.929 153.844 767.378 167.478 767.378 182.71V438.502C767.378 564.44 683.999 665.071 608.732 730.929C570.332 764.529 532.104 790.794 503.548 808.642C489.232 817.589 477.249 824.482 468.762 829.18C464.516 831.53 461.138 833.335 458.772 834.577C457.589 835.198 456.658 835.679 455.998 836.018C455.668 836.187 455.405 836.319 455.212 836.417C455.116 836.466 455.037 836.506 454.976 836.537C454.945 836.552 454.918 836.565 454.896 836.576C454.886 836.581 454.874 836.588 454.868 836.591C454.862 836.593 454.84 836.563 452.81 832.513L438.503 803.918L454.845 836.603C444.557 841.746 432.448 841.746 422.161 836.603L438.503 803.918C422.227 836.47 422.151 836.596 422.139 836.591L422.031 836.537C421.97 836.506 421.89 836.466 421.794 836.417C421.601 836.319 421.339 836.186 421.009 836.018C420.349 835.679 419.418 835.198 418.234 834.577C415.868 833.335 412.49 831.53 408.244 829.18C399.757 824.482 387.775 817.589 373.459 808.642C344.903 790.794 306.674 764.53 268.273 730.929C193.007 665.07 109.628 564.44 109.628 438.502V182.71C109.628 167.478 119.077 153.844 133.339 148.495L425.673 38.8701L426.45 38.5879ZM618.482 323.321C604.577 308.696 581.447 308.112 566.821 322.018L410.692 470.461L347.43 406.752C333.21 392.432 310.072 392.35 295.752 406.57C281.432 420.79 281.35 443.928 295.57 458.248L384.029 547.33C398.032 561.431 420.736 561.757 435.138 548.064L617.179 374.982C631.804 361.077 632.388 337.947 618.482 323.321Z"
          fill="currentColor"
        />
      </svg>

      {/* Tooltip */}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 mb-5 -translate-x-1/2 whitespace-nowrap rounded-md bg-navy px-[7px] py-3 font-sans text-[9px] font-semibold uppercase tracking-widest text-white opacity-0 shadow-e3 transition-opacity duration-150 group-hover/verified:opacity-100 group-focus-within/verified:opacity-100"
      >
        {t("verifiedProfessional")}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-full -translate-x-1/2 border-[3px] border-transparent border-t-navy"
        />
      </span>

      <span className="sr-only">{t("verifiedProfessional")}</span>
    </span>
  );
}
